import { useEffect, useState } from 'react';
import API from './axios';
import Skeleton from '../components/Skeleton';
import useCountUp from '../hooks/useCountUp';

// Component to fetch and display member count
export function MemberCount() {
  const [memberCount, setMemberCount] = useState(null);
  const displayed = useCountUp(memberCount);

  useEffect(() => {
    API.get('/members/count')
      .then((res) => setMemberCount(res.data.totalMembers))
      .catch((err) => console.error('Error fetching member count:', err));
  }, []);

  // An inline placeholder keeps the surrounding sentence from reflowing when
  // the number lands.
  if (displayed === null) {
    return <Skeleton className="skeleton--inline" as="span" />;
  }

  return <span className="tabular fade-in">{displayed}</span>;
}

// Roles that count as staff, highest first.
const allowedRoles = [
  "Owner",
  "Co‐Owner",
  "Developer",
  "Sr.Admin",
  "Admin",
  "Sr.Mod",
  "Mod",
  "Jr.Mod"
];

function getHighestRole(member) {
  if (!Array.isArray(member.roles)) return null;
  const sorted = member.roles
    .filter(role => allowedRoles.includes(role.name))
    .sort(
      (a, b) =>
        allowedRoles.indexOf(a.name) - allowedRoles.indexOf(b.name)
    );
  return sorted[0] || null;
}

export function useStaffMembers() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/members')
      .then(res => {
        const filtered = res.data.filter(member =>
          Array.isArray(member.roles) &&
          member.roles.some(role => allowedRoles.includes(role.name))
        );
        setMembers(filtered);
      })
      .catch(err => console.error('Error fetching members:', err))
      .finally(() => setLoading(false));
  }, []);

  // Sort members by their highest role's order
  const sortedMembers = [...members].sort((a, b) => {
    const aRole = getHighestRole(a);
    const bRole = getHighestRole(b);
    return (
      allowedRoles.indexOf(aRole?.name ?? "") -
      allowedRoles.indexOf(bRole?.name ?? "")
    );
  });

  return { members: sortedMembers, getHighestRole, allowedRoles, loading };
}
